import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

// Practice-configured relationship pick-list used everywhere a parent/guardian
// signs — session notes, treatment plans, parent portal. Mirrors cancel_reasons
// (label + Active/Inactive flag, managed from the Configuration page).

export interface ParentRelationship {
    id: string;
    label: string;
    active: boolean;
}

export interface ParentRelationshipWritePayload {
    label?: string;
    active?: boolean;
}

export function useParentRelationships() {
    return useQuery({
        queryKey: ['parent-relationships'] as const,
        queryFn: async () => {
            const res = await api.get<ParentRelationship[]>('/parent-relationships');
            return res.data;
        },
        staleTime: 60_000,
    });
}

/** Active list ordered by label — convenience for sign-time dropdowns that
 *  should never offer an inactive option. */
export function useActiveParentRelationships() {
    const q = useParentRelationships();
    const data = (q.data ?? [])
        .filter((r) => r.active)
        .sort((a, b) => a.label.localeCompare(b.label));
    return { ...q, data };
}

export function useCreateParentRelationship() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ParentRelationshipWritePayload) => {
            const res = await api.post<ParentRelationship>('/parent-relationships', payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['parent-relationships'] }),
    });
}

export function useUpdateParentRelationship() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ParentRelationshipWritePayload }) => {
            const res = await api.patch<ParentRelationship>(`/parent-relationships/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['parent-relationships'] }),
    });
}

export function useDeleteParentRelationship() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/parent-relationships/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['parent-relationships'] }),
    });
}
